import { IRootState } from "../../../types/redux/IRootStore";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../redux/hooks";
import { closeModalRedux } from "../../../redux/slices/modalPallets2.feature";
import AplSettingsSelector from "../../../redux/helpers/AplSettingsSelector";
import { guidIdentical, parseBool } from "../../../aplUtils/extensions";
import { AplParameter } from "../../../types/aplParameter";
import React from "react";
import { getExceptionText, translateSqlMessage, translateString } from "../../../translations/gridTranslationHelper";
import ModalConsumptionIssuingPalletStatus from "./ModalConsumptionIssuingPalletStatus";
import Spinner from "../Spinner";
import { rxgFormat } from "../../../aplUtils/resourcesHelper";
import { getIssuingRequestOffline, getIssuingRequestOnline } from "../../../aplApi/consumptionsHelper";
import { issuePalletGetPalletDetails } from "../../../redux/slices/consumptionDetails.feature";
import moment from "moment";

export default function ModalConsumptionIssuing() {
    const aplSettings = AplSettingsSelector();
    const dispatch = useAppDispatch();
    const modalPltsRedux = useSelector((state: IRootState) => state.modalPallets2);
    const authRedux = useSelector((state: IRootState) => state.auth);
    const consRedux = useSelector((state: IRootState) => state.consumptionDetails);
    const defectTypesRedux = authRedux.defectTypes;

    const [responseBlockedPalletInfo, setResponseBlockedPalletInfo] = React.useState<boolean | null>(null);
    const [responseGoodPalletInfo, setResponseGoodPalletInfo] = React.useState<boolean | null>(null);
    const [responseIsConsumed, setResponseIsConsumed] = React.useState<{ palletId: string; isConsumed: boolean }[]>([]);
    const [responseIsConsumedTemp, setResponseIsConsumedTemp] = React.useState<string[]>([]);

    const closeModal = () => {
        dispatch(closeModalRedux());
    };

    const palletResponseConsumed = (palletId: string) =>
        responseIsConsumed.some(x => guidIdentical(x.palletId, palletId) && x.isConsumed === true);
    const palletResponseNone = (palletId: string) => !responseIsConsumed.some(x => guidIdentical(x.palletId, palletId));

    const aplParamsCons = {
        aplParameterMaxIssuedPalletsConfirmConsume: aplSettings.getParam(AplParameter.MaxIssuedPalletsConfirmConsume),
        aplParameterMaxIssuedPalletsStr: aplSettings.getParam(AplParameter.MaxIssuedPallets),
        aplParameterConsumptionShowBlockedPalletInfo: aplSettings.getParam(AplParameter.ConsumptionShowBlockedPalletInfo),
        aplParameterConsumptionShowGoodPalletInfo: aplSettings.getParam(AplParameter.ConsumptionShowGoodPalletInfo)
    };

    const aplParmsConsParsed = {
        maxIssuedConfirm: parseBool(aplParamsCons.aplParameterMaxIssuedPalletsConfirmConsume),
        maxIssued: isNaN(parseInt(aplParamsCons.aplParameterMaxIssuedPalletsStr))
            ? 1
            : parseInt(aplParamsCons.aplParameterMaxIssuedPalletsStr)
    };

    const showBlockedPalletInfo =
        modalPltsRedux.consumptionState == null
            ? false
            : parseBool(aplParamsCons.aplParameterConsumptionShowBlockedPalletInfo) &&
              [5, 10, 12].includes(modalPltsRedux.consumptionState.pallet.statusId) &&
              responseBlockedPalletInfo == null;

    const showGoodPalletInfo =
        modalPltsRedux.consumptionState == null
            ? false
            : parseBool(aplParamsCons.aplParameterConsumptionShowGoodPalletInfo) &&
              ![5, 10, 12].includes(modalPltsRedux.consumptionState.pallet.statusId) &&
              responseGoodPalletInfo == null;

    const showPalletStatusCantBeIssued =
        modalPltsRedux.consumptionState == null
            ? false
            : [6, 7, 13, 14, 15, 18, 22].includes(modalPltsRedux.consumptionState.pallet.statusId);

    const issueOnlineOrConsumeOfflineCallback = React.useCallback(() => {
        let activity = consRedux.currentActivity[0];
        let pallet = modalPltsRedux.consumptionState?.pallet;
        if (activity == null || modalPltsRedux.consumptionState == null || pallet == null) return;

        if (consRedux.offline) {
            let issuuingRequestOffline = getIssuingRequestOffline(
                pallet,
                activity,
                [],
                consRedux.dateStartSelectorDate,
                consRedux.dateEndSelectorDate
            );
            dispatch(issuePalletGetPalletDetails(issuuingRequestOffline) as any);
        }

        if (!consRedux.offline) {
            let issuingRequestOnline = getIssuingRequestOnline(
                pallet,
                activity,
                aplParmsConsParsed.maxIssuedConfirm
                    ? responseIsConsumed.filter(x => x.isConsumed).map(x => x.palletId)
                    : [modalPltsRedux.consumptionState.issued.sort(x => moment(x.dateIssued).unix())[0]].map(x => x.palletId)
            );
            dispatch(issuePalletGetPalletDetails(issuingRequestOnline) as any);
        }
    }, [
        consRedux.currentActivity,
        consRedux.dateEndSelectorDate,
        consRedux.dateStartSelectorDate,
        consRedux.offline,
        dispatch,
        modalPltsRedux.consumptionState,
        responseIsConsumed,
        aplParmsConsParsed.maxIssuedConfirm
    ]);

    const okButtonRef = React.useRef<HTMLButtonElement>(null);
    const issuedWithoutResponse = !modalPltsRedux.consumptionState
        ? []
        : modalPltsRedux.consumptionState.issued.filter(x => palletResponseNone(x.palletId));

    const issuedResponseConsumed = !modalPltsRedux.consumptionState
        ? []
        : aplParmsConsParsed.maxIssuedConfirm
        ? modalPltsRedux.consumptionState.issued.filter(x => palletResponseConsumed(x.palletId))
        : [modalPltsRedux.consumptionState.issued.sort(x => moment(x.dateIssued).unix())[0]];

    const allIssuedWithoutResponse =
        modalPltsRedux.consumptionState &&
        issuedWithoutResponse.length === modalPltsRedux.consumptionState?.issued.length &&
        issuedWithoutResponse.length > 0 &&
        aplParmsConsParsed.maxIssuedConfirm;

    const confirmIfPalletHasBeenConsumed = !modalPltsRedux.consumptionState
        ? false
        : modalPltsRedux.consumptionState &&
          aplParmsConsParsed.maxIssuedConfirm &&
          (aplParmsConsParsed.maxIssued >= modalPltsRedux.consumptionState.issued.length || aplSettings.isUsSite) &&
          allIssuedWithoutResponse;

    const conditionPalletStatusCantBeIssued = showPalletStatusCantBeIssued;
    const conditionShowingBlockedPalletInfo = !modalPltsRedux.loading && modalPltsRedux.consumptionState?.pallet && showBlockedPalletInfo;
    const conditionShowingGoodPalletInfo = !modalPltsRedux.loading && modalPltsRedux.consumptionState?.pallet && showGoodPalletInfo;
    const conditionShowingError = !modalPltsRedux.loading && (modalPltsRedux.error != null || consRedux.consumptionOperationError != null);
    const conditionShowingMaxIssuedConfirmDialog =
        !consRedux.offline &&
        !modalPltsRedux.loading &&
        modalPltsRedux.consumptionState != null &&
        confirmIfPalletHasBeenConsumed &&
        allIssuedWithoutResponse;
    const conditionShowingCannotIssueAnotherPallet =
        (allIssuedWithoutResponse || issuedResponseConsumed.length === 0) &&
        modalPltsRedux.consumptionState &&
        modalPltsRedux.consumptionState.issued.length === aplParmsConsParsed.maxIssued;
    const oldestConsumedConsumptionDetail = modalPltsRedux.consumptionState?.issued
        .filter(x => palletResponseNone(x.palletId))
        .sort(x => moment(x.dateIssued).unix())[0];

    React.useEffect(() => {
        setTimeout(() => {
            if (okButtonRef && okButtonRef.current) {
                okButtonRef.current.focus();
            }
        }, 200);
    }, [modalPltsRedux]);

    React.useEffect(() => {
        if (modalPltsRedux.consumptionState) {
            console.log(
                `${new Date().toISOString()} conditionShowingGoodPalletInfo=${conditionShowingGoodPalletInfo} conditionShowingBlockedPalletInfo=${conditionShowingBlockedPalletInfo} conditionShowingMaxIssuedConfirmDialog=${conditionShowingMaxIssuedConfirmDialog} conditionShowingError =${conditionShowingError} conditionShowingCannotIssueAnotherPallet=${conditionShowingCannotIssueAnotherPallet}}`
            );
            if (
                !conditionPalletStatusCantBeIssued &&
                !conditionShowingGoodPalletInfo &&
                !conditionShowingBlockedPalletInfo &&
                !conditionShowingMaxIssuedConfirmDialog &&
                !conditionShowingError &&
                !conditionShowingCannotIssueAnotherPallet
            ) {
                issueOnlineOrConsumeOfflineCallback();
            }
        }
    }, [
        modalPltsRedux.consumptionState,
        conditionPalletStatusCantBeIssued,
        conditionShowingGoodPalletInfo,
        conditionShowingBlockedPalletInfo,
        conditionShowingMaxIssuedConfirmDialog,
        conditionShowingError,
        issueOnlineOrConsumeOfflineCallback,
        dispatch,
        conditionShowingCannotIssueAnotherPallet
    ]);

    const ModalButtons = (props: {
        okString?: string;
        cancelString?: string;
        okFunc?: () => void;
        cancelFunc?: () => void;
        hideCloseButton?: boolean;
    }) => {
        let showCloseButton = props.hideCloseButton ? false : true;
        if (modalPltsRedux.loading) return <div>...</div>;

        return (
            <React.Fragment>
                {showCloseButton && (
                    <button type="button" className="btn btn-secondary" onClick={props.cancelFunc ? props.cancelFunc : () => closeModal()}>
                        {props.cancelString
                            ? translateString(props.cancelString, aplSettings.culture)
                            : translateString("cancel", aplSettings.culture)}
                    </button>
                )}

                <button type="button" className="btn btn-primary" onClick={props.okFunc ? props.okFunc : () => {}} ref={okButtonRef}>
                    {props.okString
                        ? translateString(props.okString, aplSettings.culture)
                        : translateString("inBoundBtnOk", aplSettings.culture)}
                </button>
            </React.Fragment>
        );
    };

    const ModalHeader = () => (
        <div className="modal-header">
            <h5>{translateString("rxg_Consumption", aplSettings.culture)}</h5>
            <button
                type="button"
                className="close"
                onClick={() => {
                    closeModal();
                }}
            >
                <span aria-hidden="true" className="text-danger">
                    ×
                </span>
                <span className="sr-only">Close</span>
            </button>
        </div>
    );

    if (modalPltsRedux.loading)
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    Loading ...
                    <Spinner visible={true} marginsClass="m-5" />
                </div>
            </React.Fragment>
        );

    if (conditionPalletStatusCantBeIssued && modalPltsRedux.consumptionState) {
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    <div id="div-error" className={`alert alert-danger`} role="alert">
                        <span className="pb-3">
                            {translateSqlMessage(60042, "Pallet status not allowed for consumption", aplSettings.culture)}
                        </span>
                        <ModalConsumptionIssuingPalletStatus
                            pallet={modalPltsRedux.consumptionState?.pallet}
                            defectTypes={defectTypesRedux}
                            culture={aplSettings.culture}
                        />
                    </div>
                    <div className="modal-footer">
                        <ModalButtons
                            okFunc={() => {
                                closeModal();
                            }}
                            hideCloseButton={true}
                        />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (conditionShowingBlockedPalletInfo && modalPltsRedux.consumptionState)
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    <ModalConsumptionIssuingPalletStatus
                        pallet={modalPltsRedux.consumptionState.pallet}
                        defectTypes={defectTypesRedux}
                        culture={aplSettings.culture}
                    />
                    <div className="modal-footer">
                        <ModalButtons
                            okFunc={() => {
                                setResponseBlockedPalletInfo(true);
                            }}
                        />
                    </div>
                </div>
            </React.Fragment>
        );

    if (conditionShowingGoodPalletInfo && modalPltsRedux.consumptionState)
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    <ModalConsumptionIssuingPalletStatus
                        pallet={modalPltsRedux.consumptionState?.pallet}
                        defectTypes={defectTypesRedux}
                        culture={aplSettings.culture}
                    />
                    <div className="modal-footer">
                        <ModalButtons
                            okFunc={() => {
                                setResponseGoodPalletInfo(true);
                            }}
                        />
                    </div>
                </div>
            </React.Fragment>
        );

    if (conditionShowingMaxIssuedConfirmDialog && modalPltsRedux.consumptionState && modalPltsRedux.consumptionState.issued.length > 0) {
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    {modalPltsRedux.consumptionState.issued.length < 2 ? (
                        rxgFormat(
                            translateString("rxg_Is_the_previous_SSCC___0___completely_consumed___dialog__apl00002", aplSettings.culture),
                            [oldestConsumedConsumptionDetail!.sscc]
                        )
                    ) : (
                        <React.Fragment>
                            {translateString("haveFollowingPalletsBeenFullyUsed", aplSettings.culture)}

                            <ul className="list-group mt-2">
                                {modalPltsRedux.consumptionState.issued.map(x => (
                                    <li className="list-group-item">
                                        <div className="ml-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                onChange={e => {
                                                    if (e.currentTarget.checked)
                                                        setResponseIsConsumedTemp([...responseIsConsumedTemp, x.palletId]);
                                                    else setResponseIsConsumedTemp(responseIsConsumedTemp.filter(y => y !== x.palletId));
                                                }}
                                            />{" "}
                                            {x.sscc}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </React.Fragment>
                    )}
                </div>
                <div className="modal-footer">
                    <ModalButtons
                        okString="rxg_Yes"
                        cancelString="rxg_No"
                        okFunc={() => {
                            if (modalPltsRedux.consumptionState?.issued && modalPltsRedux.consumptionState?.issued?.length < 2) {
                                setResponseIsConsumed([
                                    ...responseIsConsumed,
                                    { palletId: oldestConsumedConsumptionDetail!.palletId, isConsumed: true }
                                ]);
                            } else {
                                setResponseIsConsumed([
                                    ...responseIsConsumed,
                                    ...responseIsConsumedTemp.map(x => ({ palletId: x, isConsumed: true }))
                                ]);
                            }
                        }}
                        cancelFunc={() => {
                            setResponseIsConsumed([
                                ...responseIsConsumed,
                                { palletId: oldestConsumedConsumptionDetail!.palletId, isConsumed: false }
                            ]);
                        }}
                    />
                </div>
            </React.Fragment>
        );
    }
    if (conditionShowingError)
        if (modalPltsRedux.error != null)
            return (
                <React.Fragment>
                    <ModalHeader />
                    <div className="modal-body">
                        <div id="div-error" className={`alert alert-danger`} role="alert">
                            {modalPltsRedux.error ? getExceptionText(modalPltsRedux.error, aplSettings.culture) : ""}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <ModalButtons
                            okFunc={() => {
                                closeModal();
                            }}
                            hideCloseButton={true}
                        />
                    </div>
                </React.Fragment>
            );
    if (consRedux.consumptionOperationError != null)
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    <div id="div-error" className={`alert alert-danger`} role="alert">
                        {consRedux.consumptionOperationError
                            ? getExceptionText(consRedux.consumptionOperationError, aplSettings.culture)
                            : ""}
                    </div>
                </div>
                <div className="modal-footer">
                    <ModalButtons
                        okFunc={() => {
                            closeModal();
                        }}
                        hideCloseButton={true}
                    />
                </div>
            </React.Fragment>
        );

    if (conditionShowingCannotIssueAnotherPallet) {
        return (
            <React.Fragment>
                <ModalHeader />
                <div className="modal-body">
                    <div>
                        {rxgFormat(translateString("youCanOnlyHaveNumberOfPalletsIssued", aplSettings.culture), [
                            aplParmsConsParsed.maxIssued.toString()
                        ])}
                    </div>
                </div>
                <div className="modal-footer">
                    <ModalButtons
                        okFunc={() => {
                            closeModal();
                        }}
                        hideCloseButton={true}
                    />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <ModalHeader />
            <div className="modal-body">
                {translateString("litWait", aplSettings.culture)} ...
                <Spinner visible={true} marginsClass="m-5" />
            </div>
        </React.Fragment>
    );
}
